import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError, api } from "../lib/api";

/**
 * A copy-paste path from "never used MCP" to a working agent (issue #42).
 *
 * The whole of it is one URL. scenix serves MCP itself and a client gets there
 * by signing in (ADR 000043), so there is nothing to install and no secret for
 * anybody to handle: the address names the space, and the token a person grants
 * reaches that space and no other (ADR 000041).
 *
 * The harness picker survives because a URL is the same everywhere but where
 * you paste it is not. Agent keys stay on the page as the other path, for
 * something that runs unattended and cannot sign in.
 */

type Harness = "claude-code" | "claude-desktop" | "claude-web" | "cursor" | "other";

const HARNESSES: { id: Harness; label: string }[] = [
  { id: "claude-code", label: "Claude Code" },
  { id: "claude-desktop", label: "Claude Desktop" },
  { id: "claude-web", label: "claude.ai" },
  { id: "cursor", label: "Cursor" },
  { id: "other", label: "Other" },
];

const EXAMPLES = [
  "What is in this space?",
  "Read the settings, then tell me what we are making.",
  "Write down what we decided about the cold open.",
  "Import this YouTube video and tell me what is in it.",
  "What questions are open on the harbour film?",
];

/** Where each harness wants the address, and what to do with it there. */
function whereToPaste(harness: Harness, url: string): { where: string; block: string } {
  switch (harness) {
    case "claude-code":
      return {
        where: "Run this once, from a terminal:",
        block: `claude mcp add --transport http scenix ${url}`,
      };
    case "claude-desktop":
      return {
        where: "Settings, then Connectors, then Add custom connector. Paste this as the URL:",
        block: url,
      };
    case "claude-web":
      return {
        where: "Settings, then Connectors, then Add custom connector. Paste this as the URL:",
        block: url,
      };
    case "cursor":
      return {
        where: "Paste into .cursor/mcp.json:",
        block: `{
  "mcpServers": {
    "scenix": {
      "url": "${url}"
    }
  }
}`,
      };
    case "other":
      return {
        where:
          "Anything that speaks MCP over HTTP takes this address. Your client will send you " +
          "here to sign in the first time it connects.",
        block: url,
      };
  }
}

export function SpaceMcpRoute() {
  const { spaceId = "" } = useParams();
  const queryClient = useQueryClient();

  const [harness, setHarness] = useState<Harness>("claude-code");
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const space = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => api.getSpace(spaceId as never),
  });

  const harnessLabel = HARNESSES.find((h) => h.id === harness)?.label ?? "Other";

  const create = useMutation({
    mutationFn: () => api.createAgentKey(spaceId as never, { name: (name || harnessLabel).trim() }),
    onSuccess: async (created) => {
      setName("");
      setCopiedKey(false);
      setFailure(null);
      setFresh(created.secret);
      await queryClient.invalidateQueries({ queryKey: ["agent-keys"] });
    },
    onError: (error) => setFailure(describe(error)),
  });

  if (space.isPending) return <p role="status">Loading…</p>;
  if (space.error) {
    return (
      <section className="empty">
        <h1>Could not load this space</h1>
        <p>
          <Link to={`/spaces/${spaceId}`}>Back</Link>
        </p>
      </section>
    );
  }

  // The address the protocol answers on, which is also what a token is bound
  // to. Built here rather than typed by anybody, because assembling it from a
  // space id by hand is the step this page exists to remove.
  const url = `${window.location.origin}/spaces/${spaceId}/mcp`;
  const paste = whereToPaste(harness, url);

  return (
    <section>
      <header className="page-head">
        <p className="crumb">
          <Link to={`/spaces/${spaceId}`}>{space.data.name}</Link>
        </p>
        <h1>Connect an agent</h1>
        <p className="muted">
          One address, and a sign-in. Nothing to install, and no key to paste anywhere: whatever you
          connect reaches {space.data.name} and nothing else.
        </p>
      </header>

      <label className="harness-picker">
        Harness
        <select
          aria-label="Harness"
          value={harness}
          onChange={(event) => setHarness(event.target.value as Harness)}
        >
          {HARNESSES.map((h) => (
            <option key={h.id} value={h.id}>
              {h.label}
            </option>
          ))}
        </select>
      </label>

      <div className="fresh-key">
        <p className="muted">{paste.where}</p>
        <pre>{paste.block}</pre>
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard.writeText(paste.block).then(() => setCopied(true));
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <p className="muted">
          The first time it connects you will be asked to sign in and to approve the connection.
          Everything it can then do is what you can do here, minus changing a cut, a note or a
          document, which stay yours.
        </p>
      </div>

      <h2 className="section-head">What to ask</h2>
      <ul>
        {EXAMPLES.map((example) => (
          <li key={example}>{example}</li>
        ))}
      </ul>

      <h2 className="section-head">Something that runs unattended</h2>
      <p className="muted">
        A script or a pipeline cannot sign in, so it holds an agent key instead. The same rules
        apply to it, and it reaches only this space.
      </p>

      {failure && (
        <p role="alert" className="error">
          {failure}
        </p>
      )}

      {fresh && (
        <div className="fresh-key">
          <p>
            <strong>Copy this now.</strong> It is not stored and cannot be shown again. If you lose
            it, revoke the key and make another.
          </p>
          <code>{fresh}</code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(fresh).then(() => setCopiedKey(true));
            }}
          >
            {copiedKey ? "Copied" : "Copy"}
          </button>
          <p className="muted">
            Send it as <code>Authorization: Bearer …</code> to the same address.
          </p>
        </div>
      )}

      <form
        className="row"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <input
          value={name}
          aria-label="What this key is for"
          placeholder="a nightly import"
          onChange={(event) => setName(event.target.value)}
        />
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? "Minting…" : "Mint a key"}
        </button>
      </form>

      <p className="muted">
        <Link to={`/spaces/${spaceId}/keys`}>Keys for this space</Link>, and{" "}
        <Link to="/settings/connectors">everything connected to your account</Link>.
      </p>
    </section>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}
